import * as fs from "../services/firestoreService";

function parseUrl(url) {
  const clean = url.replace(/^\//, "").split("?")[0];
  const parts = clean.split("/");
  const params = new URLSearchParams(url.includes("?") ? url.split("?")[1] : "");
  return { parts, params, clean };
}

export function createFirestoreApi() {
  return {
    get: async (url) => {
      const { parts, params } = parseUrl(url);
      const p0 = parts[0], p1 = parts[1], p2 = parts[2];

      // Products
      if (p0 === "products" && !p1) return { data: await fs.getProducts(params.get("search"), params.get("category")) };

      // Customers
      if (p0 === "customers" && p1 === "activity") return { data: await fs.getCustomerActivity(params.get("name")) };
      if (p0 === "customers" && !p1) return { data: await fs.getCustomers(params.get("search")) };

      // Invoices
      if (p0 === "invoices" && p1 && p2 === "print") return { data: await fs.getInvoiceWithDetails(p1, params.get("proxy_filter")) };
      if (p0 === "invoices" && p1 && !p2) return { data: await fs.getInvoiceWithDetails(p1, params.get("proxy_filter")) };
      if (p0 === "invoices" && !p1) return { data: await fs.getInvoices(params.get("search"), params.get("invoice_type"), params.get("proxy_filter")) };

      // Purchases
      if (p0 === "purchases" && !p1) return { data: await fs.getPurchases() };
      if (p0 === "purchases" && p1) return { data: await fs.getPurchase(p1) };

      // Stock registers
      if (p0 === "stock-registers" && p1 === "print-data") return { data: await fs.getStockRegisterPrintData(params.get("period"), params.get("date"), params.get("month"), params.get("year")) };
      if (p0 === "stock-registers") return { data: await fs.getStockRegisters(params.get("product_id"), params.get("category"), params.get("proxy_filter")) };

      // Dashboard
      if (p0 === "dashboard" && p1 === "summary") return { data: await fs.getDashboardSummary(params.get("period")) };
      if (p0 === "dashboard" && p1 === "sales-chart") return { data: await fs.getDashboardSalesChart(params.get("period")) };
      if (p0 === "dashboard" && p1 === "top-products") return { data: await fs.getDashboardTopProducts() };
      if (p0 === "dashboard" && p1 === "alerts") return { data: await fs.getDashboardAlerts() };

      // Expenses
      if (p0 === "expenses") return { data: await fs.getExpenses() };

      // Reports — match EXACT backend routes
      if (p0 === "reports" && p1 === "sales-profits") return { data: await fs.getSalesProfitReport(params.get("period"), params.get("start_date"), params.get("end_date")) };
      if (p0 === "reports" && p1 === "gst") return { data: await fs.getGSTReport(params.get("start_date"), params.get("end_date")) };

      // GST Reports — match backend /gst-reports/hsn-summary?month=YYYY-MM
      if (p0 === "gst-reports" && p1 === "hsn-summary") return { data: await fs.getGSTHSNReport(params.get("month")) };

      // Shop settings — match backend /settings/shop
      if (p0 === "settings" && p1 === "shop") return { data: await fs.getShopSettings() };
      if (p0 === "shop-settings") return { data: await fs.getShopSettings() };

      // Auth
      if (p0 === "auth" && p1 === "me") throw new Error("Use Firebase Auth directly");

      // Users
      if (p0 === "users") return { data: await fs.getUsers() };

      // HSN
      if (p0 === "hsn-codes") return { data: await fs.getHSNCodes() };

      console.warn("Unhandled GET:", url);
      return { data: [] };
    },

    post: async (url, data) => {
      const { parts } = parseUrl(url);
      const p0 = parts[0], p1 = parts[1], p2 = parts[2];

      if (p0 === "products") return { data: await fs.createProduct(data) };
      if (p0 === "customers") return { data: await fs.createCustomer(data) };
      if (p0 === "invoices" && p1 && p2 === "customer-paid") return { data: await fs.recordCustomerPayment(p1, data) };
      if (p0 === "invoices") return { data: await fs.createInvoice(data) };
      if (p0 === "purchases") return { data: await fs.createPurchase(data) };
      if (p0 === "expenses") return { data: await fs.createExpense(data) };
      if (p0 === "settings" && p1 === "shop") return { data: await fs.updateShopSettings(data) };
      if (p0 === "shop-settings") return { data: await fs.updateShopSettings(data) };
      if (p0 === "users") return { data: await fs.createUser(data) };

      console.warn("Unhandled POST:", url);
      return { data: {} };
    },

    put: async (url, data) => {
      const { parts } = parseUrl(url);
      const p0 = parts[0], p1 = parts[1], p2 = parts[2];

      if (p0 === "products" && p1) return { data: await fs.updateProduct(p1, data) };
      if (p0 === "customers" && p1) return { data: await fs.updateCustomer(p1, data) };
      if (p0 === "invoices" && p1 && p2 === "cancel") return { data: await fs.cancelInvoice(p1) };
      if (p0 === "invoices" && p1) return { data: await fs.updateInvoice(p1, data) };
      if (p0 === "purchases" && p1) return { data: await fs.updatePurchase(p1, data) };
      if (p0 === "expenses" && p1) return { data: await fs.updateExpense(p1, data) };
      if (p0 === "settings" && p1 === "shop") return { data: await fs.updateShopSettings(data) };
      if (p0 === "shop-settings") return { data: await fs.updateShopSettings(data) };
      if (p0 === "users" && p1) return { data: await fs.updateUser(p1, data) };

      console.warn("Unhandled PUT:", url);
      return { data: {} };
    },

    delete: async (url) => {
      const { parts } = parseUrl(url);
      const p0 = parts[0], p1 = parts[1];

      if (p0 === "products" && p1) return { data: await fs.deleteProduct(p1) };
      if (p0 === "customers" && p1) return { data: await fs.deleteCustomer(p1) };
      if (p0 === "expenses" && p1) return { data: await fs.deleteExpense(p1) };
      if (p0 === "users" && p1) return { data: await fs.deleteUser(p1) };

      console.warn("Unhandled DELETE:", url);
      return { data: {} };
    },
  };
}
